import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../partage/api/client';
import { cles } from '../partage/relectures';
import {
  cacheesParLesReglages,
  coupe,
  filtrerParTitre,
  LIGNES_AVANT_VOIR_PLUS,
  lignesQuiPassent,
  pourquoiCourt,
} from './maintenantDuPouce';
import { LigneAOuvrir } from './LigneAOuvrir';
import { requeteDesReglages, type ReglagesDeMaintenant } from './reglagesDeMaintenant';
import {
  aucuneTacheNeContient,
  cacheesParTesReglages,
  CHERCHER_UNE_AUTRE_TACHE,
  FILTRER_PAR_LE_TITRE,
  MAINTENANT,
  PAS_DE_RESEAU,
  RIEN_DE_FAISABLE_TOUT_DE_SUITE,
  VOIR_PLUS,
} from '../partage/vocabulaire';
import './ChercherUneTache.css';

/**
 * « Chercher une autre tâche », au téléphone (chantier `telephone-cadre`, ticket 02, décision 3
 * du cadrage du 2026-10-05) : ce qui est faisable, avec un champ qui filtre par le titre, **sans
 * devenir une liste interminable**. D'abord ce que tes réglages laissent passer, dix lignes puis
 * « Voir plus » ; puis ce que tes réglages cachent, replié en une ligne (« 12 cachées par tes
 * réglages »), chacune avec sa raison une fois dépliée.
 *
 * Une lecture de `/api/maintenant` avec tes réglages et « tout voir » : les lignes sous l'eau, et
 * ce que tes réglages cachent avec ce qui les cache (`cachees`, chantier nettoyage-gestes, ticket
 * 04), mis en mots par `cacheesParLesReglages`.
 */
export function ChercherUneTache({
  reglages,
  projet,
  onOuvrir,
  onRetour,
}: {
  reglages: ReglagesDeMaintenant;
  projet: string | null;
  onOuvrir: (id: string) => void;
  onRetour: () => void;
}) {
  const [texte, setTexte] = useState('');
  const [montrees, setMontrees] = useState(LIGNES_AVANT_VOIR_PLUS);
  const [cacheesOuvertes, setCacheesOuvertes] = useState(false);

  const avec = useQuery({
    queryKey: cles.maintenant(requeteDesReglages(reglages, projet, true)),
    queryFn: () => api.maintenant(requeteDesReglages(reglages, projet, true)),
  });

  const passent = avec.data ? filtrerParTitre(lignesQuiPassent(avec.data), texte) : [];
  const cachees = avec.data
    ? cacheesParLesReglages(avec.data, avec.data.reglages.temps).filter((c) => filtrerParTitre([c.ligne], texte).length > 0)
    : [];
  const { visibles, reste } = coupe(passent, montrees);

  const filtrer = (valeur: string) => {
    setTexte(valeur);
    setMontrees(LIGNES_AVANT_VOIR_PLUS);
  };

  let corps;
  if (!avec.data) {
    corps = avec.isError ? <p className="autre-tache-phrase">{PAS_DE_RESEAU}</p> : null;
  } else if (passent.length === 0 && cachees.length === 0) {
    corps = <p className="autre-tache-phrase">{texte.trim() ? aucuneTacheNeContient(texte.trim()) : RIEN_DE_FAISABLE_TOUT_DE_SUITE}</p>;
  } else {
    corps = (
      <>
        {visibles.length > 0 && (
          <ul className="autre-tache-lignes">
            {visibles.map((l) => (
              <li key={l.id}>
                <LigneAOuvrir titre={l.titre} pourquoi={pourquoiCourt(l)} prioritaire={l.prioritaire} onOuvrir={() => onOuvrir(l.id)} />
              </li>
            ))}
          </ul>
        )}
        {reste > 0 && (
          <button type="button" className="autre-tache-plus" onClick={() => setMontrees((n) => n + LIGNES_AVANT_VOIR_PLUS)}>
            {VOIR_PLUS}
          </button>
        )}
        {cachees.length > 0 && (
          <div className="autre-tache-cachees">
            <button
              type="button"
              className="autre-tache-cachees-bouton"
              aria-expanded={cacheesOuvertes}
              onClick={() => setCacheesOuvertes(!cacheesOuvertes)}
            >
              <span>{cacheesParTesReglages(cachees.length)}</span>
              <span aria-hidden="true">{cacheesOuvertes ? '▴' : '▾'}</span>
            </button>
            {cacheesOuvertes && (
              <ul className="autre-tache-lignes">
                {cachees.map((c) => (
                  <li key={c.ligne.id}>
                    <LigneAOuvrir titre={c.ligne.titre} pourquoi={c.raison} onOuvrir={() => onOuvrir(c.ligne.id)} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <section className="autre-tache"aria-label={CHERCHER_UNE_AUTRE_TACHE}>
      <button type="button" className="autre-tache-retour" onClick={onRetour}>
        ‹ {MAINTENANT}
      </button>
      <h2 className="autre-tache-titre">{CHERCHER_UNE_AUTRE_TACHE}</h2>
      <input
        type="search"
        className="autre-tache-champ"
        placeholder={FILTRER_PAR_LE_TITRE}
        aria-label={FILTRER_PAR_LE_TITRE}
        value={texte}
        onChange={(e) => filtrer(e.target.value)}
      />
      {corps}
    </section>
  );
}
